import type { ClerkProvider } from "@clerk/clerk-react";
import type { ComponentProps } from "react";

// Derived structurally from ClerkProvider's own prop, not imported from @clerk/shared by name —
// that package is a transitive dependency of @clerk/clerk-react, never declared in this
// package's own package.json, so importing its types directly would be a phantom dependency.
type ClerkAppearance = NonNullable<ComponentProps<typeof ClerkProvider>["appearance"]>;

/**
 * The token layer, once, for every Clerk component (STACK.md: "a block never ships
 * as-downloaded" — the same conform-on-entry discipline the shadcnblocks theme got, applied to
 * Clerk's own prebuilt components). Set on `<ClerkProvider>` so it reaches `<SignIn/>`,
 * `<UserButton/>`, and anything else Clerk renders — one place, not repeated per component.
 *
 * Verified against Clerk's current appearance reference (not memory), AND against the live
 * rendered DOM (computed styles, `layout.animations` alone does not touch it — that switch
 * governs popover/modal enter-exit animations, not this): `elements.userButtonTrigger`'s own
 * base stylesheet sets a fixed `transition-duration: 0.1s` for its hover state, outside CRAFT's
 * three bands. `elements` accepts a full CSS object (not just a className), so the duration
 * itself is directly overridable — confirmed live (see DESIGN.md's conformance list for the one
 * piece the API genuinely cannot reach: the default avatar's own baked-in image color).
 */
export const clerkAppearance: ClerkAppearance = {
  variables: {
    colorPrimary: "var(--primary)",
    colorBackground: "var(--card)",
    colorText: "var(--foreground)",
    colorTextSecondary: "var(--muted-foreground)",
    colorInputBackground: "var(--background)",
    colorInputText: "var(--foreground)",
    colorDanger: "var(--destructive)",
    colorNeutral: "var(--foreground)",
    borderRadius: "var(--radius-lg)",
    fontFamily: "var(--font-sans)",
  },
  elements: {
    // The default avatar (no profile image set) shows Clerk's own per-user hash color — an
    // uncontrolled, potentially bright accent that can land on red, which CRAFT §4 reserves for
    // destructive actions only. Forced to the theme's own monochrome extremes instead: this is
    // chrome, not one of the three approved emerald sites (DESIGN.md header). Only a partial
    // reach — see DESIGN.md: the loaded avatar image's own color is baked into server-generated
    // pixels, not CSS.
    avatarBox: { backgroundColor: "var(--foreground)" },
    avatarImage: { backgroundColor: "var(--foreground)" },
    userButtonAvatarBox: { backgroundColor: "var(--foreground)" },
    userButtonAvatarImage: { backgroundColor: "var(--foreground)" },
    // CRAFT §1: "the only three values" (150/200/250ms). The trigger's hover transition is
    // 100ms outside the box — retuned to the micro tier (150ms: "hovers, checkmarks, fades").
    userButtonTrigger: { transitionDuration: "150ms" },
  },
  layout: {
    // Governs Clerk's own popover/modal enter-exit animations (a different mechanism from the
    // element-level transition above). Off collapses them rather than leaving an unaudited
    // duration in place — consistent with, though not required by, CRAFT's reduced-motion
    // collapse.
    animations: false,
    shimmer: false,
  },
};
